import { useEffect, useState } from "react";
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { McpAppEntry } from "~/components/assistant-v2/useMcpAppEvents";
import { apiClient } from "~/utils/apiClient";

const CE_MCP_APP_ENABLED = import.meta.env.VITE_CE_MCP_APP_ENABLED === "true";
const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface McpAppRegistryEntry {
  tool_call_name: string;
  server_id: string;
  resource_uri: string;
  title: string;
}

interface McpAppRegistryResponse {
  entries: McpAppRegistryEntry[];
}

/**
 * Hydrates MCP app entries from historical tool calls after conversation
 * history loads. Fetches the app registry to determine which tool names
 * have MCP app UIs, then fetches the HTML for each matching tool call.
 */
export function useMcpAppHydration(
  initialMessages: ThreadMessageLike[] | null | undefined,
  isLoadingHistory: boolean
): Map<string, McpAppEntry> {
  const [entries, setEntries] = useState<Map<string, McpAppEntry>>(() => new Map());

  useEffect(() => {
    if (!CE_MCP_APP_ENABLED || isLoadingHistory || !initialMessages?.length) return;

    let cancelled = false;

    (async () => {
      let registry: McpAppRegistryResponse;
      try {
        registry = await apiClient<McpAppRegistryResponse>(
          `${VITE_ANALYSIS_AGENT_API}/mcp-app-registry`
        );
      } catch {
        return;
      }

      if (cancelled || !registry.entries.length) return;

      const registryMap = new Map(registry.entries.map((e) => [e.tool_call_name, e]));

      const toolCalls: { id: string; entry: McpAppRegistryEntry; result: unknown }[] = [];
      for (const msg of initialMessages) {
        if (msg.role !== "assistant" || !Array.isArray(msg.content)) continue;
        for (const part of msg.content) {
          if (typeof part !== "object" || part === null) continue;
          const p = part as {
            type?: string;
            toolName?: string;
            toolCallId?: string;
            result?: string | null;
          };
          if (p.type !== "tool-call" || !p.toolName || !p.toolCallId) continue;
          const regEntry = registryMap.get(p.toolName);
          if (regEntry) {
            let parsedResult: unknown = p.result;
            if (typeof p.result === "string") {
              try {
                parsedResult = JSON.parse(p.result);
              } catch {
                // keep as string
              }
            }
            toolCalls.push({ id: p.toolCallId, entry: regEntry, result: parsedResult });
          }
        }
      }

      if (cancelled || toolCalls.length === 0) return;

      const hydrated = new Map<string, McpAppEntry>();

      await Promise.all(
        toolCalls.map(async ({ id, entry, result }) => {
          const params = new URLSearchParams({
            server_id: entry.server_id,
            uri: entry.resource_uri,
          });
          const url = `${VITE_ANALYSIS_AGENT_API}/mcp-resources/fetch?${params}`;
          try {
            const html = await apiClient<string>(url, { expectsText: true });
            hydrated.set(id, { html, title: entry.title, toolResult: result });
          } catch {
            hydrated.set(id, { html: null, title: entry.title, toolResult: result });
          }
        })
      );

      if (!cancelled) {
        setEntries(hydrated);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialMessages, isLoadingHistory]);

  return entries;
}
